import { useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { CheckCircle2, AlertCircle, Loader2 } from "@/lib/icons"
import { Button } from "@/components/ui/button"

const EVO_URL = import.meta.env.VITE_EVOLUTION_API_URL
const EVO_KEY = import.meta.env.VITE_EVOLUTION_API_KEY

const STATE_LABEL = {
  open: "Conectado",
  close: "Desconectado",
  connecting: "Conectando…",
  qr: "Esperando escaneo",
}

async function evoFetch(path, opts = {}) {
  const res = await fetch(`${EVO_URL}${path}`, {
    method: opts.method || "GET",
    headers: {
      apikey: EVO_KEY,
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
  return res.json()
}

export default function QRSharePage() {
  const { instance } = useParams()
  const [qrBase64, setQrBase64] = useState(null)
  const [pairingCode, setPairingCode] = useState(null)
  const [state, setState] = useState(null)
  const [error, setError] = useState(null)
  const [refreshAt, setRefreshAt] = useState(Date.now())
  const stoppedRef = useRef(false)

  // Determinar nombre legible (mapeo amigable)
  const displayName =
    instance === "tepeyec-gaby"
      ? "Gaby Silva"
      : instance === "tepeyact-produccion"
        ? "Lulú"
        : instance

  const isConnected = state === "open"

  const loadQR = async () => {
    if (stoppedRef.current) return
    setError(null)
    try {
      const data = await evoFetch(`/instance/connect/${encodeURIComponent(instance)}`)
      setQrBase64(data?.base64 || data?.qrcode?.base64 || null)
      setPairingCode(data?.pairingCode || null)
    } catch (e) {
      setError(`No se pudo cargar el QR: ${e.message}`)
    }
  }

  const checkState = async () => {
    if (stoppedRef.current) return
    try {
      const s = await evoFetch(`/instance/connectionState/${encodeURIComponent(instance)}`)
      const st = s?.instance?.state || s?.state || null
      setState(st)
      if (st === "open") {
        // Conectado, dejar de pedir QR
        stoppedRef.current = true
      }
    } catch {
      // silencio: seguimos intentando
    }
  }

  useEffect(() => {
    if (!instance) return
    stoppedRef.current = false
    setQrBase64(null)
    setPairingCode(null)
    setState(null)
    setError(null)
    checkState()
    loadQR()
    // Refresh QR cada 25s (los QR de WhatsApp expiran ~30s)
    const qrTimer = setInterval(() => {
      if (stoppedRef.current) return
      setRefreshAt(Date.now())
      loadQR()
    }, 25000)
    // Poll estado cada 4s para detectar conexión exitosa
    const stateTimer = setInterval(checkState, 4000)
    return () => {
      stoppedRef.current = true
      clearInterval(qrTimer)
      clearInterval(stateTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance])

  const onManualRefresh = () => {
    stoppedRef.current = false
    setRefreshAt(Date.now())
    loadQR()
    checkState()
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-emerald-50/40 to-slate-100 dark:from-slate-950 dark:via-emerald-950/20 dark:to-slate-900 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200/80 dark:border-slate-800/80 overflow-hidden">
          {/* Header verde estilo WhatsApp */}
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 px-6 py-7 text-white">
            <p className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">
              Conectar WhatsApp
            </p>
            <h1 className="text-2xl font-bold mt-1">{displayName}</h1>
            <p className="text-sm opacity-90 mt-1">Instituto Tepeyac · Admisiones</p>
          </div>

          {/* Cuerpo */}
          <div className="p-6">
            {isConnected ? (
              <div className="flex flex-col items-center text-center py-8 gap-4">
                <div className="h-20 w-20 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-emerald-700 dark:text-emerald-300">
                  ¡Conectado correctamente!
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Ya puedes cerrar esta página. WhatsApp está vinculado.
                </p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center text-center py-8 gap-4">
                <div className="h-16 w-16 rounded-full bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center">
                  <AlertCircle className="h-10 w-10 text-rose-600 dark:text-rose-400" />
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-300">{error}</p>
                <Button onClick={onManualRefresh} variant="default">
                  Reintentar
                </Button>
              </div>
            ) : qrBase64 ? (
              <div className="flex flex-col items-center gap-4">
                <ol className="text-sm text-slate-700 dark:text-slate-300 space-y-1.5 self-start mb-2">
                  <li>1. Abre WhatsApp en tu teléfono</li>
                  <li>2. Toca <span className="font-semibold">⋮ → Dispositivos vinculados</span></li>
                  <li>3. Toca <span className="font-semibold">Vincular un dispositivo</span></li>
                  <li>4. Apunta tu teléfono al código de abajo</li>
                </ol>
                <div className="rounded-2xl bg-white p-3 ring-2 ring-emerald-200 dark:ring-emerald-800 shadow-md">
                  <img
                    key={refreshAt}
                    src={qrBase64}
                    alt="QR de WhatsApp"
                    className="w-64 h-64"
                    style={{ imageRendering: "pixelated" }}
                  />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  El código se renueva automáticamente cada 25 s.
                </p>
                {pairingCode && (
                  <div className="w-full mt-2 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300">
                      O ingresa este código:
                    </p>
                    <p className="font-mono text-xl font-bold tracking-[0.3em] text-emerald-700 dark:text-emerald-400 mt-1">
                      {pairingCode}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center text-center py-12 gap-4">
                <Loader2 className="h-10 w-10 text-emerald-500 animate-spin" />
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Generando código QR…
                </p>
              </div>
            )}

            {/* Estado actual + refresh */}
            {!isConnected && (
              <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Estado: <span className="font-semibold">{STATE_LABEL[state] || "—"}</span>
                </div>
                <Button onClick={onManualRefresh} variant="ghost" size="sm" className="text-xs">
                  Actualizar
                </Button>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 dark:text-slate-400 mt-6">
          Si tienes problemas, contacta a tu administrador.
        </p>
      </div>
    </div>
  )
}
